
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { getNetworkColor } from './NetworkColorUtils';
import { NodeData } from './NetworkTypes';
import { NetworkLegend } from './NetworkLegend';

interface NetworkChartProps {
  chartData: any[];
  nodes: NodeData[];
  networkGroups: Record<string, NodeData[]>;
  isDark: boolean;
  isMobile: boolean;
}

export const NetworkChart: React.FC<NetworkChartProps> = ({
  chartData,
  nodes,
  networkGroups,
  isDark,
  isMobile
}) => {
  const renderCustomizedLegend = () => {
    return <NetworkLegend networkGroups={networkGroups} isDark={isDark} />;
  };

  const formatTooltip = (value: any, name: string) => {
    const nodeName = nodes.find(node => node.id === name)?.name || name;
    return [`${value}`, nodeName];
  };

  // Transform data for the block chart
  const transformDataForBlockChart = () => {
    if (!chartData.length) return [];
    
    // Get the last data point (current values)
    const currentData = chartData[chartData.length - 1];
    
    // Transform to format suitable for bar chart
    return Object.keys(currentData)
      .filter(key => key !== 'time') // Filter out the time key
      .map(nodeId => {
        const node = nodes.find(n => n.id === nodeId);
        return {
          name: node?.name || nodeId,
          value: currentData[nodeId],
          network: node?.network || 'unknown'
        };
      });
  };

  const blockData = transformDataForBlockChart();

  return (
    <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
      <BarChart
        data={blockData}
        margin={isMobile ? { top: 5, right: 5, left: 0, bottom: 5 } : { top: 5, right: 20, left: 0, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#ddd'} />
        <XAxis 
          dataKey="name" 
          tick={{ fontSize: isMobile ? 10 : 12, fill: isDark ? '#f0f0f0' : '#333333' }} 
          axisLine={{ stroke: isDark ? '#444' : '#ccc' }} 
          tickLine={{ stroke: isDark ? '#444' : '#ccc' }}
          angle={-45}
          textAnchor="end"
          height={80}
        />
        <YAxis 
          tick={{ fontSize: isMobile ? 10 : 12, fill: isDark ? '#f0f0f0' : '#333333' }} 
          axisLine={{ stroke: isDark ? '#444' : '#ccc' }} 
          tickLine={{ stroke: isDark ? '#444' : '#ccc' }}
          width={30}
          domain={[0, 120]}
        />
        <Tooltip 
          contentStyle={{ 
            background: isDark ? 'rgba(51, 51, 51, 0.8)' : 'rgba(255, 255, 255, 0.8)', 
            border: 'none', 
            borderRadius: 8,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            color: isDark ? '#f0f0f0' : '#333333',
            fontSize: isMobile ? '0.75rem' : '0.875rem',
            padding: isMobile ? '4px 8px' : '8px 12px'
          }}
          formatter={(value, name, props) => {
            if (name === 'value') {
              return [`${value}`, 'Signal Strength'];
            }
            return [value, name];
          }}
          labelFormatter={(label) => `${label}`}
        />
        <Legend content={renderCustomizedLegend} />
        <Bar 
          dataKey="value" 
          radius={[4, 4, 0, 0]}
        >
          {blockData.map((entry, index) => (
            <Cell 
              key={`cell-${index}`} 
              fill={getNetworkColor(entry.network, index, isDark)} 
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};
